import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { debounce } from "lodash";
import {
  Breadcrumb,
  Button,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  Tooltip,
} from "antd";
import {
  CarOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";

import { getRequest } from "../../../axios";
import { ShowErrorMsg } from "../../../utils/Notifications";
import "../../../styles/Main.scss";
import SearchIcon from "../../../image/SearchIcon.svg";
import DocumentPreviewModal from "./DocumentPreviewModal";

const { Search } = Input;
const statusOptionStyles = {
  "Material Sent - Acknowledgment Pending": {
    color: "#0958d9",
    background: "#e6f4ff",
    borderColor: "#91caff",
  },
  "Distribution completed": {
    color: "#08979c",
    background: "#e6fffb",
    borderColor: "#87e8de",
  },
  "Received in full": {
    color: "#389e0d",
    background: "#f6ffed",
    borderColor: "#b7eb8f",
  },
  "Partially received": {
    color: "#d46b08",
    background: "#fff7e6",
    borderColor: "#ffd591",
  },
};

const StatusOptionTag = ({ value, label }) => {
  const style = statusOptionStyles[value] || {
    color: "#595959",
    background: "#fafafa",
    borderColor: "#d9d9d9",
  };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 12px",
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 500,
        color: style.color,
        background: style.background,
        border: `1px solid ${style.borderColor}`,
      }}
    >
      {label}
    </span>
  );
};

StatusOptionTag.propTypes = {
  value: PropTypes.string,
  label: PropTypes.string,
};

const InboundMaterialShipments = ({
  embedded,
  refreshKey,
  onConfirmPhysicalReceipt,
} = {}) => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [state, setState] = useState({
    page: 1,
    pagesize: 10,
    search: "",
    status: "",
    start_date: "",
  });
  const [filterSelection, setFilterSelection] = useState({
    search: "",
    status: "",
  });
  const [searchInputValue, setSearchInputValue] = useState("");
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userName, setUserName] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [documentPreview, setDocumentPreview] = useState(null);

  const openDocumentPreview = (record) => {
    if (!record?.document_file_url) return;
    setDocumentPreview({ url: record.document_file_url });
  };

  const onSearch = (value) => {
    setState((prev) => ({ ...prev, search: value ?? "", page: 1 }));
  };
  const debouncedSearchRef = useRef(
    debounce((value) => onSearch(value), 500),
  ).current;
  const searchCallbackDelayed = (e) => {
    const value = e?.target?.value ?? "";
    setSearchInputValue(value);
    debouncedSearchRef(value);
  };

  const applyFilters = () => {
    const dateStr =
      selectedDate && typeof selectedDate.format === "function"
        ? selectedDate.format("YYYY-MM-DD")
        : "";
    setState((prev) => ({
      ...prev,
      search: searchInputValue.trim(),
      status: filterSelection.status ?? "",
      start_date: dateStr,
      page: 1,
    }));
  };

  const resetFilters = () => {
    setFilterSelection({ search: "", status: "" });
    setSearchInputValue("");
    setSelectedDate(null);
    setState((prev) => ({
      ...prev,
      search: "",
      status: "",
      start_date: "",
      page: 1,
    }));
  };

  const fetchDistributions = async () => {
    try {
      setLoading(true);
      const params = { page: state.page, pagesize: state.pagesize };
      if (state.search?.trim()) params.search = state.search.trim();
      if (state.status && state.status !== "") params.status = state.status;
      if (state.start_date?.trim()) params.start_date = state.start_date.trim();
      const res = await getRequest(
        "inventory-goods-receipts/distributions/",
        params,
      );
      const payload = res?.data;
      const count = payload?.count ?? 0;
      const results = payload?.results ?? {};
      const data = results?.data ?? [];
      const docList = Array.isArray(data) ? data : [];

      setTotalCount(count);
      if (results?.user_name) setUserName(results.user_name);
      const mapped = docList.map((item, index) => ({
        key: `${item.document_id}-${item.site_id}-${index}`,
        id:
          item.document_id && item.site_id
            ? `${item.document_id}-${item.site_id}`
            : index,
        document_id: item.document_id,
        document_name: item.document_name,
        document_file_url: item.document_file_url,
        invoice_number: item.invoice_number,
        challan_number: item.challan_number,
        is_challan: !!item.is_challan,
        grn_number: item.grn_number,
        site_id: item.site_id,
        site_name: item.site_name,
        total_site_items: item.total_site_items,
        status: item.status ?? "—",
        is_finalized: !!item.is_finalized,
        distributed_by_id:
          item?.destribution?.distributed_by_id ?? item.distributed_by_id,
        distributed_by:
          item?.destribution?.distributed_by ?? item.distributed_by,
        distributed_by_email:
          item?.destribution?.destribution_by_email ?? null,
        distributed_by_phone:
          item?.destribution?.destribution_by_phone ?? null,
        distribution_date: item.distribution_date,
      }));
      setDistributions(mapped);
    } catch (err) {
      console.log(err);
      ShowErrorMsg(err?.response?.data?.error || "Not Found");
      setDistributions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistributions();
  }, [state]);

  const prevRefreshKeyRef = useRef(undefined);
  useEffect(() => {
    if (!embedded || refreshKey == null) return;
    const prev = prevRefreshKeyRef.current;
    prevRefreshKeyRef.current = refreshKey;
    if (prev === undefined) return;
    fetchDistributions();
  }, [embedded, refreshKey]);

  const handleTableChange = (pagination) => {
    setState((prev) => ({
      ...prev,
      page: pagination.current,
      pagesize: pagination.pageSize,
    }));
  };

  const handleViewConfirmReceipt = (record) => {
    const documentId = record.document_id;
    const siteId = record.site_id;
    const isFinalized = !!record.is_finalized;
    if (documentId == null) return;
    if (embedded && onConfirmPhysicalReceipt) {
      onConfirmPhysicalReceipt(documentId, siteId, isFinalized);
    } else {
      const query = siteId != null ? `?site_id=${siteId}` : "";
      navigate(`/store/inbound/${documentId}${query}`);
    }
  };

  const columns = [
    {
      title: "Challan Ref. /Invoice Ref.",
      dataIndex: "invoice_number",
      key: "invoice_number",
      width: 200,
      render: (_, record) => (
        <span
          style={{
            fontWeight: 600,
            color: record?.is_challan ? "#d46b08" : "#094e8f",
          }}
        >
          {record?.is_challan
            ? (record?.challan_number ?? "—")
            : (record?.invoice_number ?? "—")}
        </span>
      ),
    },
    {
      title: "GRN Ref.",
      dataIndex: "grn_number",
      key: "grn_number",
      render: (value) => (
        <span style={{ fontWeight: "bold", color: "#094e8f" }}>
          {value ?? "—"}
        </span>
      ),
    },
    {
      title: "Site",
      dataIndex: "site_name",
      key: "site_name",
      render: (value) => <span>{value ?? "—"}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 220,
      render: (_, record) => {
        const status = record.status ?? "—";
        return <StatusOptionTag value={status} label={status} />;
      },
    },
    {
      title: "Distribution Date",
      dataIndex: "distribution_date",
      key: "distribution_date",
      render: (value) => <span>{value ?? "—"}</span>,
    },
    {
      title: "Distributed By",
      dataIndex: "distributed_by",
      key: "distributed_by",
      render: (value, record) => {
        const name = value ?? "N/A";
        const email = record?.distributed_by_email ?? null;
        const phone = record?.distributed_by_phone ?? null;
        const hasMeta = !!(email || phone);
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span>{name}</span>
            {hasMeta ? (
              <Tooltip
                placement="top"
                color="#094E8F"
                title={
                  <div style={{ lineHeight: 1.5 }}>
                    <div>
                      <strong>Email:</strong> {email || "—"}
                    </div>
                    <div>
                      <strong>Mobile:</strong> {phone || "—"}
                    </div>
                  </div>
                }
              >
                <InfoCircleOutlined style={{ color: "#094e8f", cursor: "pointer" }} />
              </Tooltip>
            ) : null}
          </span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 140,
      align: "center",
      render: (_, record) => {
        const iconStyle = {
          fontSize: 18,
          cursor: "pointer",
          color: "#094e8f",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        };
        const viewDocumentIcon = record.document_file_url ? (
          <Tooltip key="doc" title="View document">
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              <FileTextOutlined
                style={iconStyle}
                onClick={() => openDocumentPreview(record)}
              />
            </span>
          </Tooltip>
        ) : null;
        const viewConfirmLabel = record.is_finalized
          ? "View"
          : "View & Confirm Receipt";
        const ViewConfirmIcon = record.is_finalized
          ? EyeOutlined
          : CheckCircleOutlined;
        return (
          <Space
            size="middle"
            align="center"
            style={{ display: "inline-flex", alignItems: "center" }}
          >
            {viewDocumentIcon}
            <Tooltip title={viewConfirmLabel}>
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <ViewConfirmIcon
                  style={iconStyle}
                  onClick={() => handleViewConfirmReceipt(record)}
                />
              </span>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <section className="grid-sec mt-3 tablegridsec">
        <div className="PR-body">
          <div className="po-good-header">
            <div className="po-header-details" style={{ margin: "0" }}>
              <div className="po-row">
                <strong>User:</strong> <span>{userName || "—"}</span>
              </div>
            </div>
            <Space size={10} wrap>
              <div className="searchwrp">
                <Space direction="vertical">
                  <Search
                    allowClear
                    value={searchInputValue}
                    onChange={searchCallbackDelayed}
                    placeholder="Search "
                    prefix={
                      <img
                        src={SearchIcon}
                        alt="SearchIcon"
                        className="SearchIcon"
                        width={20}
                      />
                    }
                    style={{ minWidth: 260 }}
                  />
                </Space>
              </div>

              <Select
                placeholder="Select Status"
                allowClear
                value={filterSelection.status || undefined}
                onChange={(val) =>
                  setFilterSelection((prev) => ({
                    ...prev,
                    status: val ?? "",
                  }))
                }
                style={{ width: 240 }}
                optionLabelProp="value"
                options={[
                  {
                    value: "Material Sent - Acknowledgment Pending",
                    label: (
                      <StatusOptionTag
                        value="Material Sent - Acknowledgment Pending"
                        label="Acknowledgment Pending"
                      />
                    ),
                  },
                  {
                    value: "Received in full",
                    label: (
                      <StatusOptionTag
                        value="Received in full"
                        label="Received in full"
                      />
                    ),
                  },
                  {
                    value: "Partially received",
                    label: (
                      <StatusOptionTag
                        value="Partially received"
                        label="Partially received"
                      />
                    ),
                  },
                ]}
              />

              <DatePicker
                value={selectedDate}
                onChange={(val) => setSelectedDate(val ? moment(val) : null)}
                placeholder="Select date"
              />

              <Button
                type="primary"
                icon={
                  <Tooltip title="Apply filters">
                    <FilterOutlined style={{ fontSize: "20px" }} />
                  </Tooltip>
                }
                onClick={applyFilters}
                style={{
                  backgroundColor: "#094e8f",
                  color: "#ffffff",
                  border: "none",
                  width: "40px",
                  height: "40px",
                  boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                  borderRadius: "4px",
                }}
              />
              <Button
                type="primary"
                icon={
                  <Tooltip title="Reset filters">
                    <UndoOutlined style={{ fontSize: "20px" }} />
                  </Tooltip>
                }
                onClick={resetFilters}
                style={{
                  backgroundColor: "#094e8f",
                  color: "#ffffff",
                  border: "none",
                  width: "40px",
                  height: "40px",
                  boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                  borderRadius: "4px",
                }}
              />
            </Space>
          </div>
          <div className="tablewrp">
            <Table
              loading={loading}
              columns={columns}
              bordered
              dataSource={distributions}
              rowKey={(record) => record.id ?? record.key}
              onChange={handleTableChange}
              pagination={{
                current: state.page,
                pageSize: state.pagesize,
                total: totalCount,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                showTotal: (total) => `Total ${total} items`,
              }}
              scroll={{ x: 900 }}
              locale={{
                emptyText: (
                  <div className="text-center">
                    No inbound shipments found for the selected filters.
                  </div>
                ),
              }}
            />
          </div>
        </div>
      </section>

      <DocumentPreviewModal
        preview={documentPreview}
        onClose={() => setDocumentPreview(null)}
      />
    </>
  );
};

InboundMaterialShipments.propTypes = {
  embedded: PropTypes.bool,
  refreshKey: PropTypes.number,
  onConfirmPhysicalReceipt: PropTypes.func,
};

export default InboundMaterialShipments;
